import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const ThemeCtx = createContext(null);
const STORAGE_KEY = "argus.theme";

function getSystemPref() {
  return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

function applyTheme(mode) {
  const effective = mode === "auto" ? getSystemPref() : mode;
  const root = document.documentElement;
  root.classList.remove("dark", "light");
  root.classList.add(effective);
  root.setAttribute("data-theme", effective);
  return effective;
}

export function ThemeProvider({ children, defaultMode = "dark" }) {
  const [mode, setMode] = useState(() => {
    if (typeof window === "undefined") return defaultMode;
    return localStorage.getItem(STORAGE_KEY) || defaultMode;
  });
  const [effective, setEffective] = useState(() =>
    mode === "auto" ? getSystemPref() : mode
  );

  useEffect(() => {
    setEffective(applyTheme(mode));
    localStorage.setItem(STORAGE_KEY, mode);
    if (mode !== "auto") return undefined;
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const onChange = () => setEffective(applyTheme("auto"));
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [mode]);

  const setTheme = useCallback((next) => setMode(next), []);
  const cycle = useCallback(() => {
    setMode((m) => (m === "dark" ? "light" : m === "light" ? "auto" : "dark"));
  }, []);

  return (
    <ThemeCtx.Provider value={{ mode, effective, setTheme, cycle }}>
      {children}
    </ThemeCtx.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeCtx);
  if (!ctx) throw new Error("useTheme must be used inside ThemeProvider");
  return ctx;
}
